import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, Star, ArrowRight, Sparkles, Trophy } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const CITY_INFO: Record<string, { name: string; tagline: string; hero: string }> = {
  bangalore: { name: "Bangalore", tagline: "Rooftops in Indiranagar, craft cocktails in Koramangala, late nights on Brigade Road.", hero: "the pub capital of India" },
  mumbai: { name: "Mumbai", tagline: "Bandra speakeasies, Lower Parel lounges, sunrise after-parties.", hero: "the city that never sleeps" },
  delhi: { name: "Delhi", tagline: "Hauz Khas Village, CP rooftops, Aerocity hotel bars.", hero: "north India's nightlife capital" },
  hyderabad: { name: "Hyderabad", tagline: "Jubilee Hills lounges, Banjara Hills cocktail bars.", hero: "the new south nightlife magnet" },
  pune: { name: "Pune", tagline: "Koregaon Park nights, Kalyani Nagar rooftops.", hero: "the youth nightlife heartland" },
  goa: { name: "Goa", tagline: "Beach clubs, sunset bars, sundown techno.", hero: "India's eternal party state" },
  chennai: { name: "Chennai", tagline: "Nungambakkam lounges, ECR beach bars.", hero: "Chennai's emerging late-night scene" },
};

const CityLanding = () => {
  const { slug } = useParams<{ slug: string }>();
  const info = CITY_INFO[slug?.toLowerCase() || ""];
  const [venues, setVenues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!info) { setLoading(false); return; }
    supabase.from("venues").select("*").eq("city", info.name).eq("is_active", true).order("rating", { ascending: false }).limit(12)
      .then(({ data }) => { setVenues(data || []); setLoading(false); });
  }, [slug, info]);

  if (!info) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-center px-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">City not found</h1>
          <Link to="/venues" className="mt-4 inline-block text-primary">Browse all venues</Link>
        </div>
      </div>
    );
  }

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Best nightlife venues in ${info.name}`,
    itemListElement: venues.slice(0, 10).map((v, i) => ({
      "@type": "ListItem", position: i + 1,
      item: { "@type": "BarOrPub", name: v.name, address: v.address, aggregateRating: { "@type": "AggregateRating", ratingValue: v.rating, reviewCount: v.total_ratings || 1 } },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`Nightlife in ${info.name} — Krew`}
        description={`Discover the best clubs, rooftops, and lounges in ${info.name}. Plan a night out with your crew on Krew.`}
        canonical={`https://krew.rest/city/${slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />

      <header className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> {info.hero}
            </p>
            <h1 className="text-4xl font-bold text-foreground sm:text-6xl">
              Nightlife in <span className="text-gradient-neon">{info.name}</span>
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground font-body">{info.tagline}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/create-krew" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground glow-neon">
                Plan a night out <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/venues" className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:border-primary/30">
                Browse all venues
              </Link>
            </div>
          </motion.div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Top spots in {info.name}</h2>
          <Link to={`/leaderboard`} className="inline-flex items-center gap-1 text-sm text-secondary"><Trophy className="h-4 w-4" /> City leaderboard</Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-64 animate-pulse rounded-2xl bg-card" />)}
          </div>
        ) : venues.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center">
            <p className="text-muted-foreground">No active venues in {info.name} yet.</p>
            <Link to="/vendor" className="mt-3 inline-block text-primary text-sm">List your venue →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {venues.map((v, i) => (
              <motion.div key={v.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/venue/${v.slug}`} className="group block overflow-hidden rounded-2xl border border-border bg-card transition-all hover:border-primary/30">
                  <div className="aspect-video overflow-hidden">
                    <img src={v.image_url || "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=600"} alt={v.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">{v.name}</h3>
                      <span className="flex items-center gap-1 text-sm"><Star className="h-3.5 w-3.5 text-secondary" />{v.rating}</span>
                    </div>
                    <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{v.area}</p>
                    <p className="mt-2 text-xs text-muted-foreground">₹{v.avg_price_per_person}/person · {v.category}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
          <h2 className="text-3xl font-bold text-foreground">Stop the WhatsApp chaos</h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground font-body">
            Krew locks your crew in with a commitment deposit, picks the venue with AI, and refunds everyone who shows up. Plan your next {info.name} night in under 2 minutes.
          </p>
          <Link to="/create-krew" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground glow-neon">
            Start a Krew <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CityLanding;